import React, { useMemo } from 'react';
import { Link, usePage } from '@inertiajs/react';
import styled from 'styled-components';
import GTMEventManager from '@/app/GTMEventManager.js';
import LogoOnly from '@assets/logo_only_white.svg?react';

const FixedLogoCenter = styled(LogoOnly)`
    width: 160%;
    transform: translateY(calc(100% - 146px));
    @media (min-width: 640px) {
        width: 75%;
        transform: translateY(calc(100% - 106px));
    }
`;
const FixedLogoLeft = styled(LogoOnly)`
    width: 160%;
    transform: translateX(-85%) translateY(calc(-100% + 200px));
    @media (min-width: 640px) {
        width: 75%;
        transform: translateX(-50%) translateY(calc(-100% + 240px));
    }
`;
const FixedLogoRight = styled(LogoOnly)`
    width: 160%;
    transform: translateX(85%) translateY(calc(-100% + 200px));
    @media (min-width: 640px) {
        width: 75%;
        transform: translateX(50%) translateY(calc(-100% + 240px));
    }
`;

const Footer = () => {
    const locale = usePage().props.language;
    const { settings } = usePage().props;

    const yearsRange = useMemo(() => {
        const createdYear = 2023;
        const currentYear = parseInt((new Date()).getFullYear());

        if (createdYear === currentYear) return createdYear;

        return `${createdYear}-${currentYear}`;
    }, []);

    return (
        <footer
            id="main-footer"
            className="flex flex-col pt-20 md:pt-12 pb-20 sm:pb-20 lg:pb-0 sm:pt-14 3xl:pt-20 relative overflow-hidden bg-very-dark-gray border-t border-light-gray"
            style={{marginTop: '-0.25rem'}}
        >
            <div className="flex items-center justify-center">
                <Link href={route('booking.create')} className="relative z-10">
                    <LogoOnly className="size-10 sm:size-10 3xl:size-16 mb-4 md:mb-0 text-light-gray"/>
                </Link>
                <FixedLogoCenter className="absolute opacity-5 bottom-0 text-light-gray"/>
                <FixedLogoLeft className="absolute opacity-5 left-0 top-0 text-light-gray"/>
                <FixedLogoRight className="absolute opacity-5 right-0 top-0 text-light-gray"/>
                <div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: '0',
                        left: '0',
                        boxShadow: 'rgba(0, 0, 0, 0.75) 0px 14px 25px 0px inset'
                    }}
                />
            </div>
            <div
                className="flex flex-col gap-4 md:gap-0 md:flex-row justify-between items-center px-6 3xl:px-8 py-4 sm:pt-12 text-xs 3xl:text-sm text-very-light-gray tracking-wide relative z-10">
                <ul className="flex flex-row gap-6">
                    <li className="cursor-pointer underline md:no-underline hover:underline">
                        <a href={locale === 'fr' ? 'https://thegatevr.io/conditions/' : 'https://thegatevr.io/en/terms-and-conditions/'}
                           target="_blank">
                            {__('book.common.footer.terms')}
                        </a>
                    </li>
                    <li className="cursor-pointer underline md:no-underline hover:underline">
                        <a href={locale === 'fr' ? 'https://thegatevr.io/vie-privee/' : 'https://thegatevr.io/en/privacy-policy/'}
                           target="_blank">
                            {__('book.common.footer.policy')}
                        </a>
                    </li>
                </ul>
                <div className="flex flex-col items-center md:flex-row gap-4">
                    <div className="flex flex-grow items-center gap-1">
                        <svg className="size-4 text-light-gray" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 256 256">
                            <rect width="256" height="256" fill="none"/>
                            <polyline points="224 56 128 144 32 56" fill="none" stroke="currentColor"
                                      strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                            <path d="M32,56H224a0,0,0,0,1,0,0V192a8,8,0,0,1-8,8H40a8,8,0,0,1-8-8V56A0,0,0,0,1,32,56Z"
                                  fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                                  strokeWidth="16"/>
                            <line x1="110.55" y1="128" x2="34.47" y2="197.74" fill="none" stroke="currentColor"
                                  strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                            <line x1="221.53" y1="197.74" x2="145.45" y2="128" fill="none" stroke="currentColor"
                                  strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                        </svg>

                        <a
                            className="hover:underline"
                            href={`mailto:${settings.contact.email}`}
                            onClick={() => GTMEventManager.contact('email', 'footer')}
                        >
                            <span>{settings.contact.email}</span>
                        </a>
                    </div>
                    <div className="flex flex-grow items-center gap-1">
                        <svg className="size-4 text-light-gray" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                            <rect width="256" height="256" fill="none"/>
                            <path d="M164.39,145.34a8,8,0,0,1,7.59-.69l47.16,21.13a8,8,0,0,1,4.8,8.3A48.33,48.33,0,0,1,176,216,136,136,0,0,1,40,80,48.33,48.33,0,0,1,81.92,32.06a8,8,0,0,1,8.3,4.8l21.13,47.2a8,8,0,0,1-.66,7.53L89.32,117a7.93,7.93,0,0,0-.54,7.81c8.27,16.93,25.77,34.22,42.75,42.41a7.92,7.92,0,0,0,7.83-.59Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="18"/>
                        </svg>

                        <a
                            className="hover:underline"
                            href={`tel:${settings.contact.phone.replace(/\s/g, '')}`}
                            onClick={() => GTMEventManager.contact('email', 'footer')}
                        >
                            <span>{settings.contact.phone}</span>
                        </a>
                    </div>
                    <span>{yearsRange} © The Gate VR</span>
                </div>
            </div>
        </footer>
    )
}

export default Footer;
